import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type { SiteRedirectDestination, SiteSpace } from '@gitbook/api-client';
import type { HiveClient } from '@gitbook/hive-client';
import { useHiveClient } from '@gitbook/hive-react';
import { createPalette, PaletteButton, type PaletteDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import type { ButtonProps } from '@gitbook/spine-react';
import { assert, assertNever } from '@gitbook/util-ts';

import { createSpacePagesGroup, SpaceIcon } from '../../../../../Components';
import { apiQuery, flattenGroupedSections } from '../../../../../state';
import { getPropsFromResolvedDestination, siteRedirectDestinationResolutionQuery } from './util';

export type SiteRedirectDestinationPickerProps = {
    organizationId: string;
    siteId: string;
    value: SiteRedirectDestination | null;
    onChange: (value: SiteRedirectDestination | null) => void;
};

export const SiteRedirectDestinationPicker = React.forwardRef(
    function SiteRedirectDestinationPicker(
        props: {
            organizationId: string;
            siteId: string;
            value: SiteRedirectDestination | null;
            onChange: (value: SiteRedirectDestination | null) => void;
        } & Omit<ButtonProps, 'label' | 'leadingIcon' | 'trailingIcon' | 'value' | 'onChange'>,
        ref: React.ForwardedRef<HTMLButtonElement>
    ) {
        const { organizationId, siteId, value, onChange, ...buttonProps } = props;
        const { data: siteStructure } = useSuspenseQuery(
            apiQuery('getSiteStructure', { organizationId, siteId })
        );
        const hiveClient = useHiveClient();
        const { data: resolvedValue, isLoading } = useQuery(
            siteRedirectDestinationResolutionQuery({
                siteStructure,
                siteRedirectDestination: value ?? null,
                hiveClient,
            })
        );
        const palette = () => {
            switch (siteStructure.type) {
                case 'sections':
                    return createPalette({
                        groups: [
                            {
                                key: 'sections',
                                title: 'Sections',
                                commands: flattenGroupedSections(siteStructure.structure).map(
                                    (section) => ({
                                        key: section.id,
                                        title: section.title,
                                        trigger: () => {
                                            onChange({
                                                kind: 'site-section',
                                                siteSectionId: section.id,
                                            });
                                        },
                                        descendant: () =>
                                            getSiteSpacesPalette({
                                                siteSpaces: section.siteSpaces,
                                                hiveClient,
                                                onChange,
                                            }),
                                    })
                                ),
                            },
                        ],
                    });
                case 'siteSpaces':
                    return getSiteSpacesPalette({
                        siteSpaces: siteStructure.structure,
                        hiveClient,
                        onChange,
                    });
                default:
                    assertNever(siteStructure);
            }
        };

        const resolvedDestinationProps = resolvedValue
            ? getPropsFromResolvedDestination(resolvedValue)
            : null;

        return (
            <PaletteButton
                ref={ref}
                kind="secondary"
                label="Choose a destination content"
                {...resolvedDestinationProps}
                palette={palette}
                trailingIcon={Icon.ChevronDown}
                {...buttonProps}
                disabled={isLoading || buttonProps.disabled}
            />
        );
    }
);

function getSiteSpacesPalette(input: {
    siteSpaces: SiteSpace[];
    hiveClient: HiveClient;
    onChange: (value: SiteRedirectDestination | null) => void;
}): PaletteDef {
    const { siteSpaces, hiveClient, onChange } = input;
    return createPalette({
        groups: [
            {
                key: 'site-spaces',
                title: 'Variants',
                commands: siteSpaces.map((siteSpace) => ({
                    key: siteSpace.id,
                    title: siteSpace.title,
                    icon: <SpaceIcon space={siteSpace.space} />,
                    trigger: () => {
                        onChange({ kind: 'site-space', siteSpaceId: siteSpace.id });
                    },
                    descendant: () => {
                        return createPalette({
                            search: { placeholder: 'Find pages…' },
                            groups: [
                                createSpacePagesGroup({
                                    content: {
                                        spaceId: siteSpace.space.id,
                                        changeRequestId: undefined,
                                    },
                                    hiveClient,
                                    pick: (contentRef) => {
                                        assert('page' in contentRef && contentRef.page);
                                        onChange({
                                            kind: 'site-page',
                                            siteSpaceId: siteSpace.id,
                                            pageId: contentRef.page,
                                        });
                                    },
                                    options: {
                                        accept: ['page'],
                                        targetCurrentSpace: false,
                                    },
                                }),
                            ],
                        });
                    },
                })),
            },
        ],
    });
}
